import CheckboxInput from 'LuxKit/Checkbox/CheckboxInput'
import Expander from 'components/utils/Expander'
import { MAX_COMPARISON_SELECTIONS } from 'constants/offer'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { addOfferComparisonSelection, removeOfferComparisonSelection, replaceOfferComparisonSelection } from 'actions/OfferComparisonActions'
import { selectOfferComparisonEnabled } from 'selectors/offerComparisonSelectors'
import uuidV4 from 'lib/string/uuidV4Utils'
import { stopPropagation } from 'lib/web/eventHandlerUtils'
import { rem } from 'polished'
import { useCallback, useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import useOfferList from 'hooks/Offers/useOfferList'
import equal from 'fast-deep-equal'
import Tooltip from 'LuxKit/Tooltip'

const Root = styled.div`
  display: inline-block;
  padding-bottom: ${rem(12)};
`

interface Props {
  offerType: App.OfferType
  offerId: string
  name: string
  filters?: App.OfferListFilters
  priceTotals?: {
    price: number
    memberPrice: number
    value: number
  }
}

function SearchTileComparisonCheckbox({ offerType, offerId, name, filters, priceTotals }: Props) {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const currentSelections = useAppSelector(state => state.offerComparison.selections)
  const comparisonEnabled = useAppSelector(selectOfferComparisonEnabled)
  const canAddSelection = currentSelections.length < MAX_COMPARISON_SELECTIONS
  const searchId = useOfferList(filters).searchId

  const [selectionId] = useState(() => uuidV4())

  const currentSelection = currentSelections.find(s => s.offerId === offerId)
  const isSelected = !!currentSelection

  const onToggle = useCallback(() => {
    if (currentSelection) {
      dispatch(removeOfferComparisonSelection(currentSelection.selectionId))
    } else {
      dispatch(addOfferComparisonSelection({ selectionId, offerType, offerId, name, priceTotals, params: { filters, searchId } }))
    }
  }, [dispatch, offerType, selectionId, offerId, name, filters, searchId, currentSelection, priceTotals])

  useEffect(() => {
    if (currentSelection) {
      // Replace selection if the details have changed
      const newSelection: App.OfferComparisonSelection = { selectionId, offerType, offerId, name, priceTotals, params: { filters, searchId } }
      if (!equal(currentSelection, newSelection)) {
        dispatch(replaceOfferComparisonSelection(newSelection))
      }
    }
  }, [currentSelection, dispatch, filters, name, offerId, offerType, priceTotals, searchId, selectionId])

  const checkboxDisabled = !isSelected && !canAddSelection

  const tooltipDescription = intl.formatMessage({
    id: 'vpSAosJNVNC5kKYIy2hq',
    defaultMessage: 'Only {maxSelections} offers can be compared at a time',
    description: 'Tooltip warning when maximum offer comparison selections reached',
  }, { maxSelections: MAX_COMPARISON_SELECTIONS })

  const compareLabel = intl.formatMessage({
    id: 'qpw7rI3XvxC4TsexOR0r',
    defaultMessage: 'Compare',
    description: 'Checkbox label for adding offer to comparison on search tile',
  })

  return (
    <Expander expanded={comparisonEnabled}>
      <Tooltip description={tooltipDescription} disabled={!comparisonEnabled || !checkboxDisabled}>
        <Root onClick={stopPropagation} aria-hidden={!comparisonEnabled}>
          <CheckboxInput
            onChange={onToggle}
            disabled={checkboxDisabled || !comparisonEnabled}
            checked={isSelected}
          >
            {compareLabel}
          </CheckboxInput>
        </Root>
      </Tooltip>
    </Expander>
  )
}

export default SearchTileComparisonCheckbox
